import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useShell } from '../shell/shellContext';
import { useTheme } from './ThemeProvider';

// The account half of the appearance override (REQ-282 AC2).
//
// There are two stores on purpose, and they answer two different questions:
//   · `localStorage` (`sl.theme`) answers "what should `<html data-theme>` be BEFORE the first paint?" — the
//     inline script in index.html reads it, which is what keeps a dark user from a white flash.
//   · `UserPreferences.theme` (API §12.8) answers "what did this person choose?", so another browser they
//     sign into opens the same way.
//
// This hook is the bridge in the one direction the inline script cannot cover: a browser that has never been
// told anything locally adopts the account's saved choice, once, on the first load. A device that HAS a
// local override is left alone — it is the more specific answer, and it is the one that already painted.

export function useSavedTheme(): void {
  const { queries } = useShell();
  const theme = useTheme();
  const saved = useQuery(queries.preferences());
  const applied = useRef(false);
  const preference = saved.data?.data.theme;

  useEffect(() => {
    if (applied.current || preference === undefined) return;
    applied.current = true;
    // `system` is also what "nothing stored" reads as, so there is nothing to adopt in that case.
    if (preference !== 'system' && theme.preference === 'system') {
      theme.setPreference(preference);
    }
  }, [preference, theme]);
}
